type ArabicNoun = {
  one: string
  two: readonly [nominative: string, oblique: string]
  few: string
  many: string
  other: string
}

const nouns = {
  booking: { one: 'حجز واحد', two: ['حجزان', 'حجزين'], few: 'حجوزات', many: 'حجزًا', other: 'حجز' },
  service: { one: 'خدمة واحدة', two: ['خدمتان', 'خدمتين'], few: 'خدمات', many: 'خدمة', other: 'خدمة' },
  resource: { one: 'خيار واحد', two: ['خياران', 'خيارين'], few: 'خيارات', many: 'خيارًا', other: 'خيار' },
  customer: { one: 'عميل واحد', two: ['عميلان', 'عميلين'], few: 'عملاء', many: 'عميلًا', other: 'عميل' },
  business: { one: 'منشأة واحدة', two: ['منشأتان', 'منشأتين'], few: 'منشآت', many: 'منشأة', other: 'منشأة' },
  second: { one: 'ثانية واحدة', two: ['ثانيتان', 'ثانيتين'], few: 'ثوانٍ', many: 'ثانية', other: 'ثانية' },
  minute: { one: 'دقيقة واحدة', two: ['دقيقتان', 'دقيقتين'], few: 'دقائق', many: 'دقيقة', other: 'دقيقة' },
  hour: { one: 'ساعة واحدة', two: ['ساعتان', 'ساعتين'], few: 'ساعات', many: 'ساعة', other: 'ساعة' },
  day: { one: 'يوم واحد', two: ['يومان', 'يومين'], few: 'أيام', many: 'يومًا', other: 'يوم' },
  price: { one: 'سعر واحد', two: ['سعران', 'سعرين'], few: 'أسعار', many: 'سعرًا', other: 'سعر' },
  visitor: { one: 'زائر واحد', two: ['زائران', 'زائرين'], few: 'زوار', many: 'زائرًا', other: 'زائر' },
  user: { one: 'مستخدم واحد', two: ['مستخدمان', 'مستخدمين'], few: 'مستخدمين', many: 'مستخدمًا', other: 'مستخدم' },
  participant: { one: 'مشارك واحد', two: ['مشاركان', 'مشاركين'], few: 'مشاركين', many: 'مشاركًا', other: 'مشارك' },
  person: { one: 'شخص واحد', two: ['شخصان', 'شخصين'], few: 'أشخاص', many: 'شخصًا', other: 'شخص' },
  location: { one: 'فرع واحد', two: ['فرعان', 'فرعين'], few: 'فروع', many: 'فرعًا', other: 'فرع' },
  seat: { one: 'مقعد واحد', two: ['مقعدان', 'مقعدين'], few: 'مقاعد', many: 'مقعدًا', other: 'مقعد' },
  record: { one: 'سجل واحد', two: ['سجلان', 'سجلين'], few: 'سجلات', many: 'سجلًا', other: 'سجل' },
  subscription: { one: 'اشتراك واحد', two: ['اشتراكان', 'اشتراكين'], few: 'اشتراكات', many: 'اشتراكًا', other: 'اشتراك' },
  agreement: { one: 'اتفاق واحد', two: ['اتفاقان', 'اتفاقين'], few: 'اتفاقات', many: 'اتفاقًا', other: 'اتفاق' },
} as const satisfies Record<string, ArabicNoun>

export type ArabicCountUnit = keyof typeof nouns
const pluralRules = new Intl.PluralRules('ar')

/** Plain text for nonnegative whole counts; Latin digits match Seaa's booking UI. */
export function arabicCount(count: number, unit: ArabicCountUnit, options: { case?: 'nominative' | 'oblique' } = {}): string {
  // Missing/invalid metrics must not masquerade as zero or a singular item.
  if (!Number.isSafeInteger(count) || count < 0) return '—'
  const noun = nouns[unit]
  const category = pluralRules.select(count)
  if (category === 'one') return noun.one
  if (category === 'two') return noun.two[options.case === 'oblique' ? 1 : 0]
  if (category === 'few') return `${count} ${noun.few}`
  if (category === 'many') return `${count} ${noun.many}`
  return `${count} ${noun.other}`
}
